/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import Layout from "../components/Layout";
import api, { pollO365WithProgress } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";
import { exportRowsToXlsx, xlsxDate } from "../lib/exportXlsx";
import { useAuth } from "../contexts/AuthContext";
import { useRefresh } from "../contexts/RefreshContext";
import { MagnifyingGlass, EnvelopeSimple, CalendarBlank, X, CaretRight, CaretDown, CaretUp, Funnel, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";

const BORDER = "1px solid var(--jde-border)";
const th = { padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#475569", textAlign: "left", background: "#f1f5f9", borderBottom: BORDER, whiteSpace: "nowrap" };
const td = { padding: "8px 12px", fontSize: 13, color: "#0f172a", borderBottom: BORDER, verticalAlign: "middle" };
const fmtNum = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

// Days overdue vs the Must Ship By date for cargo not yet shipped.
// Returns a positive integer when past due, or null when still on time / no date.
const delayDays = (msb) => {
  if (!msb) return null;
  const d = new Date(String(msb).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - d.getTime()) / 86400000);
  return diff > 0 ? diff : null;
};

const packLabel = (r) => {
  const bits = [];
  if (r.tot_pallets) bits.push(`${r.tot_pallets} pallet${r.tot_pallets > 1 ? "s" : ""}`);
  if (r.tot_cartons) bits.push(`${r.tot_cartons} carton${r.tot_cartons > 1 ? "s" : ""}`);
  return bits.join(" · ") || "—";
};

function SortTh({ label, k, align, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  const justify = align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";
  return (
    <th style={{ ...th, textAlign: align || "left", cursor: "pointer", userSelect: "none" }}
      data-testid={`cfs-sort-${k}`} onClick={() => onSort(k)} title="Click to sort">
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: justify, width: "100%", color: active ? "#0f172a" : undefined }}>
        {label}
        {active
          ? (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)
          : <CaretDown size={11} weight="bold" style={{ opacity: 0.25 }} />}
      </span>
    </th>
  );
}

function EmailModal({ rows, onClose }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/cfs/email-defaults")
      .then((r) => { setTo((r.data.default_to || []).join(", ")); setCc((r.data.default_cc || []).join(", ")); })
      .catch(() => {});
  }, []);

  const send = () => {
    const toArr = to.split(",").map((s) => s.trim()).filter(Boolean);
    const ccArr = cc.split(",").map((s) => s.trim()).filter(Boolean);
    if (!toArr.length) { toast.error("Add at least one recipient (To)"); return; }
    setSending(true);
    api.post("/cfs/email", { ms_ponos: rows.map((r) => r.ms_pono), to: toArr, cc: ccArr })
      .then((r) => { toast.success(`CFS cut-off email sent (${r.data.pos} PO${r.data.pos > 1 ? "s" : ""})`); onClose(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Send failed"))
      .finally(() => setSending(false));
  };

  const inp = { width: "100%", padding: "8px 10px", fontSize: 13, border: BORDER, borderRadius: 8, marginTop: 4 };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} data-testid="cfs-email-modal">
      <div style={{ width: 560, maxWidth: "100%", background: "white", borderRadius: 14, padding: 20, boxShadow: "0 20px 50px rgba(0,0,0,.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Email CFS Cut-Off</h3>
          <button onClick={onClose} data-testid="cfs-email-close" style={{ border: "none", background: "none", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
          One email with a table of <b>{rows.length} PO{rows.length > 1 ? "s" : ""}</b> — item numbers, cartons/pallets, due dates, CFS cut-off & must-ship-by.
        </div>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>To (comma-separated)
          <input data-testid="cfs-email-to" value={to} onChange={(e) => setTo(e.target.value)} style={inp} placeholder="logistics@company.com" />
        </label>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#475569", display: "block", marginTop: 12 }}>Cc
          <input data-testid="cfs-email-cc" value={cc} onChange={(e) => setCc(e.target.value)} style={inp} />
        </label>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", fontSize: 13, borderRadius: 8, border: BORDER, background: "white", cursor: "pointer" }}>Cancel</button>
          <button onClick={send} disabled={sending} data-testid="cfs-email-send" style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8, border: "none", background: "var(--omh-primary,#2A5C9B)", color: "white", cursor: "pointer", opacity: sending ? .6 : 1 }}>
            {sending ? "Sending…" : `Send email (${rows.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function CutoffCell({ testid, value, saving, onSave, canEdit = true }) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); try { inputRef.current.showPicker?.(); } catch (_) {} } }, [editing]);

  // Non-logistics users see the cut-off read-only (no edit / no clear) — enforced server-side too.
  if (!canEdit) {
    return value
      ? <span data-testid={testid} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" }}>{fmtDate(value)}</span>
      : <span data-testid={testid} style={{ fontSize: 12, color: "#cbd5e1" }}>—</span>;
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="date"
        data-testid={testid}
        defaultValue={value || ""}
        disabled={saving}
        onChange={(e) => { onSave(e.target.value); setEditing(false); }}
        onBlur={() => setEditing(false)}
        style={{ padding: "5px 8px", fontSize: 13, border: BORDER, borderRadius: 7, background: value ? "#fff7ed" : "white" }}
      />
    );
  }
  if (value) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <button
          data-testid={testid}
          onClick={() => setEditing(true)}
          disabled={saving}
          title="Edit CFS cut-off"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", fontSize: 13, fontWeight: 700, color: "#b45309", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 7, cursor: "pointer", whiteSpace: "nowrap" }}
        >
          <CalendarBlank size={13} weight="bold" /> {fmtDate(value)}
        </button>
        <button
          data-testid={`${testid}-clear`}
          onClick={() => onSave("")}
          disabled={saving}
          title="Clear cut-off"
          style={{ border: "none", background: "none", cursor: "pointer", color: "#94a3b8", lineHeight: 0, padding: 2 }}
        >
          <X size={13} weight="bold" />
        </button>
      </span>
    );
  }
  return (
    <button
      data-testid={testid}
      onClick={() => setEditing(true)}
      disabled={saving}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, color: "#475569", background: "white", border: `1px dashed var(--jde-border)`, borderRadius: 7, cursor: "pointer", whiteSpace: "nowrap" }}
    >
      <CalendarBlank size={13} weight="bold" /> Set date
    </button>
  );
}

// Per-item CY/CFS shipment splits editor (shown inside a PO's expanded detail).
function SplitsEditor({ row, onSaved, canEdit = true }) {
  const items = row.items || [];
  const firstIcode = items[0]?.icode || "";
  const [splits, setSplits] = useState(() => (row.splits || []).map((s) => ({ ...s })));
  const [saving, setSaving] = useState(false);

  const msbOf = (mode, date) => {
    if (!date) return null;
    if (String(mode).toUpperCase() === "CFS") {
      const d = new Date(date + "T00:00:00"); d.setDate(d.getDate() - 4);
      return d.toISOString().slice(0, 10);
    }
    return date;
  };
  const setField = (i, k, v) => setSplits((arr) => arr.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const addSplit = (mode) => setSplits((arr) => [...arr, { icode: firstIcode, iname: items.find((it) => it.icode === firstIcode)?.iname || "", mode, qty: "", date: "" }]);
  const removeSplit = (i) => setSplits((arr) => arr.filter((_, j) => j !== i));

  // per-item reconcile: sum of split qty vs open qty
  const reconcile = items.map((it) => {
    const assigned = splits.filter((s) => (s.icode || "") === (it.icode || "")).reduce((a, s) => a + (Number(s.qty) || 0), 0);
    return { icode: it.icode, open: Number(it.open_qty) || 0, assigned, ok: Math.abs(assigned - (Number(it.open_qty) || 0)) < 0.001 || assigned === 0 };
  });
  const mismatches = reconcile.filter((r) => !r.ok);

  const save = () => {
    for (const s of splits) {
      if (!s.mode) { toast.error("Each split needs a mode (CY/CFS)"); return; }
      if (!(Number(s.qty) > 0)) { toast.error("Each split needs a quantity > 0"); return; }
    }
    setSaving(true);
    api.put("/cfs/splits", {
      ms_pono: row.ms_pono,
      splits: splits.map((s) => ({ icode: s.icode || "", iname: items.find((it) => it.icode === s.icode)?.iname || s.iname || "", mode: String(s.mode).toUpperCase(), qty: Number(s.qty) || 0, date: s.date || null })),
    }).then(() => { toast.success("Shipment splits saved"); onSaved && onSaved(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Save failed"))
      .finally(() => setSaving(false));
  };

  const sel = { padding: "5px 8px", fontSize: 12.5, border: BORDER, borderRadius: 7, background: "white" };
  const modeChip = (m) => ({ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 800, background: m === "CY" ? "#e0f2fe" : "#fef3c7", color: m === "CY" ? "#0369a1" : "#b45309" });

  return (
    <div data-testid={`cfs-splits-${row.ms_pono}`} style={{ marginTop: 12, padding: 12, background: "white", border: BORDER, borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>Shipment splits (CY / CFS)</div>
        {canEdit && (
        <div style={{ display: "flex", gap: 6 }}>
          <button data-testid={`cfs-add-cy-${row.ms_pono}`} onClick={() => addSplit("CY")} style={{ padding: "5px 10px", fontSize: 12, fontWeight: 700, borderRadius: 7, border: "1px solid #bae6fd", background: "#e0f2fe", color: "#0369a1", cursor: "pointer" }}>+ CY</button>
          <button data-testid={`cfs-add-cfs-${row.ms_pono}`} onClick={() => addSplit("CFS")} style={{ padding: "5px 10px", fontSize: 12, fontWeight: 700, borderRadius: 7, border: "1px solid #fde68a", background: "#fef3c7", color: "#b45309", cursor: "pointer" }}>+ CFS</button>
        </div>
        )}
      </div>
      {splits.length === 0 && <div style={{ fontSize: 12, color: "#94a3b8", padding: "6px 0" }}>{canEdit ? "No splits yet. Add a CY (full container) and/or CFS (consolidated) split above." : "No CY / CFS splits recorded for this PO."}</div>}
      {splits.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            {["Item", "Mode", "Qty", "Date", "Must Ship By", "Delay", ""].map((h, i) => (
              <th key={i} style={{ ...th, background: "#f8fafc", textAlign: i >= 2 && i <= 5 ? "center" : "left" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {splits.map((s, i) => {
              const msb = msbOf(s.mode, s.date);
              const dl = delayDays(msb);
              return (
                <tr key={i}>
                  <td style={td}>
                    <select data-testid={`cfs-split-item-${row.ms_pono}-${i}`} value={s.icode || ""} disabled={!canEdit} onChange={(e) => setField(i, "icode", e.target.value)} style={sel}>
                      {items.map((it, k) => <option key={k} value={it.icode || ""}>{it.icode || "—"}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <select data-testid={`cfs-split-mode-${row.ms_pono}-${i}`} value={s.mode} disabled={!canEdit} onChange={(e) => setField(i, "mode", e.target.value)} style={sel}>
                      <option value="CY">CY</option>
                      <option value="CFS">CFS</option>
                    </select>
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <input data-testid={`cfs-split-qty-${row.ms_pono}-${i}`} type="number" value={s.qty} disabled={!canEdit} onChange={(e) => setField(i, "qty", e.target.value)} style={{ ...sel, width: 90, textAlign: "right" }} />
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <input data-testid={`cfs-split-date-${row.ms_pono}-${i}`} type="date" value={s.date || ""} disabled={!canEdit} onChange={(e) => setField(i, "date", e.target.value)} style={sel} />
                  </td>
                  <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap", fontWeight: 700, color: msb ? "#b45309" : "#cbd5e1" }}>
                    <span style={modeChip(String(s.mode).toUpperCase())}>{String(s.mode).toUpperCase()}</span>{" "}{msb ? fmtDate(msb) : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {dl != null ? <span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 800, background: "#fee2e2", color: "#b91c1c" }}>{dl}d late</span> : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {canEdit && (
                    <button data-testid={`cfs-split-remove-${row.ms_pono}-${i}`} onClick={() => removeSplit(i)} title="Remove split" style={{ border: "none", background: "none", cursor: "pointer", color: "#94a3b8", lineHeight: 0 }}><X size={14} weight="bold" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {mismatches.length > 0 && (
        <div data-testid={`cfs-splits-warn-${row.ms_pono}`} style={{ marginTop: 8, fontSize: 11.5, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 7, padding: "6px 10px" }}>
          Quantities don't reconcile: {mismatches.map((m) => `${m.icode} ${m.assigned}/${m.open}`).join(" · ")}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
        {canEdit ? (
        <button data-testid={`cfs-splits-save-${row.ms_pono}`} onClick={save} disabled={saving} style={{ padding: "7px 16px", fontSize: 12.5, fontWeight: 700, borderRadius: 8, border: "none", background: "var(--omh-primary,#2A5C9B)", color: "white", cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : "Save splits"}
        </button>
        ) : (
        <span style={{ fontSize: 11, color: "#94a3b8" }}>Read-only · CY/CFS splits are managed by Logistics</span>
        )}
      </div>
    </div>
  );
}


function InlineCartingApprove({ c, pono, candidates, allowManual, onApprove, onDismiss, onView }) {
  const cands = candidates || [];
  const [date, setDate] = useState(c.cfs_cutoff_date || "");
  const [selPo, setSelPo] = useState(pono || (cands[0] && cands[0].ms_pono) || "");
  const [busy, setBusy] = useState(false);
  const targetPo = pono || selPo;

  const approve = () => {
    if (!targetPo) { toast.error("Pick a PO to attach the cut-off"); return; }
    if (!date) { toast.error("Enter the CFS cut-off date"); return; }
    setBusy(true);
    onApprove(c.id, targetPo, date).catch(() => {}).finally(() => setBusy(false));
  };
  const dismiss = () => { setBusy(true); onDismiss(c.id).catch(() => {}).finally(() => setBusy(false)); };

  const pill = { display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde047", flexWrap: "wrap", justifyContent: "center" };
  const btn = (bg, fg, bd) => ({ padding: "3px 9px", fontSize: 11, fontWeight: 700, borderRadius: 6, border: bd || "none", background: bg, color: fg, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, whiteSpace: "nowrap" });
  const mini = { fontSize: 11, padding: "2px 6px", border: BORDER, borderRadius: 5, background: "white" };

  return (
    <div data-testid={`carting-inline-${c.id}`} style={pill}
      title={`From carting order email · PO ${c.po_number || "\u2014"}${c.consignee ? " \u00b7 " + c.consignee : ""}${c.booking_no ? " \u00b7 booking " + c.booking_no : ""}`}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e", whiteSpace: "nowrap" }}>Carting order{c.cfs_cutoff_date ? ` · ${fmtDate(c.cfs_cutoff_date)}` : ""}</span>
      {allowManual && (
        cands.length > 0 ? (
          <select data-testid={`carting-inline-po-${c.id}`} value={selPo} onChange={(e) => setSelPo(e.target.value)} style={mini}>
            {cands.map((m) => <option key={m.ms_pono} value={m.ms_pono}>{m.customer_name} · {m.po_number}{m.match_field === "custpo2" ? " (end)" : ""}</option>)}
          </select>
        ) : (
          <input data-testid={`carting-inline-po-${c.id}`} value={selPo} onChange={(e) => setSelPo(e.target.value)} placeholder="ms_pono" style={{ ...mini, width: 88 }} />
        )
      )}
      <input data-testid={`carting-inline-date-${c.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} style={mini} />
      {c.has_image && <button data-testid={`carting-inline-view-${c.id}`} onClick={() => onView(c.id)} style={btn("#e2e8f0", "#334155")}>View</button>}
      <button data-testid={`carting-inline-approve-${c.id}`} onClick={approve} disabled={busy} style={btn("#15803d", "white")}>Approve</button>
      <button data-testid={`carting-inline-dismiss-${c.id}`} onClick={dismiss} disabled={busy} style={btn("white", "#b91c1c", BORDER)} title="Dismiss">✕</button>
    </div>
  );
}

// Chip: has this PO's CFS cut-off been communicated to the team by email — and is it current?
function EmailStateChip({ state, lastSentAt }) {
  if (!state || state === "none") return null;
  const base = { display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap", lineHeight: 1.6 };
  if (state === "emailed") {
    return (
      <span data-testid="cfs-email-chip" data-state="emailed" style={{ ...base, background: "#dcfce7", color: "#166534", border: "1px solid #86efac" }}
        title={lastSentAt ? `Emailed to team on ${fmtDate(lastSentAt)}` : "Emailed to team"}>
        ✓ Emailed{lastSentAt ? ` ${fmtDate(lastSentAt)}` : ""}
      </span>
    );
  }
  if (state === "reemail_needed") {
    return (
      <span data-testid="cfs-email-chip" data-state="reemail_needed" style={{ ...base, background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" }}
        title={`Cut-off was revised after the last email${lastSentAt ? ` (sent ${fmtDate(lastSentAt)})` : ""} — re-notify the team`}>
        ⚠ Re-email needed
      </span>
    );
  }
  return (
    <span data-testid="cfs-email-chip" data-state="not_emailed" style={{ ...base, background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0" }}
      title="This CFS cut-off has not been emailed to the team yet">
      Not emailed
    </span>
  );
}

// Chip: a newer carting order proposes a DIFFERENT cut-off than the one currently saved.
function RevisedChip({ revision }) {
  if (!revision) return null;
  return (
    <span data-testid="cfs-revised-chip" style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap", lineHeight: 1.6, background: "#ffedd5", color: "#9a3412", border: "1px solid #fdba74" }}
      title={`A newer carting order proposes a different CFS cut-off — review & approve below`}>
      ⟳ Revised: {fmtDate(revision.old)} → {fmtDate(revision.new)}
    </span>
  );
}


export default function CfsCutoff() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { tick } = useRefresh();
  const [customer, setCustomer] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [emailOpen, setEmailOpen] = useState(false);
  const [savingPono, setSavingPono] = useState(null);
  const [carting, setCarting] = useState([]);
  const { user } = useAuth();
  // Logistics historically had the FULL cut-off workflow (Pull Mailbox, Suggested/Revised/With-cutoff,
  // cut-off dates + editing, Email/approve, Emailed state). Managers keep the simplified planning view.
  const isAdmin = ["admin", "super_admin", "logistics"].includes(user?.role);
  const canEdit = user?.role === "logistics";  // CFS cut-off workflow is logistics-only (set/edit/clear, email, approve/reject)
  const strictAdmin = ["admin", "super_admin"].includes(user?.role);  // backfill: backend is require_admin
  const [backfillDate, setBackfillDate] = useState("");
  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = () => {
    if (!backfillDate) { toast.error("Pick a date to backfill carting emails from"); return; }
    setBackfilling(true);
    api.post("/cfs/carting/backfill", { since_date: backfillDate })
      .then(() => {
        toast.success("Backfill started — reading the mailbox…");
        const timer = setInterval(() => {
          api.get("/cfs/carting/backfill-status").then((r) => {
            const s = r.data || {};
            if (!s.running) {
              clearInterval(timer);
              setBackfilling(false);
              if (s.error) toast.error(`Backfill failed: ${s.error}`);
              else toast.success(`Backfill done — ${s.created ?? 0} new cut-off(s) from ${s.scanned ?? 0} carting email(s)`);
              loadCarting();
            }
          }).catch(() => { clearInterval(timer); setBackfilling(false); });
        }, 3000);
      })
      .catch((e) => { setBackfilling(false); toast.error(e?.response?.data?.detail || "Backfill failed to start"); });
  };
  const [polling, setPolling] = useState(false);
  const pollMailbox = () => {
    setPolling(true);
    pollO365WithProgress()
      .then((d) => {
        const rec = d.recovered_carting ? `, recovered ${d.recovered_carting}` : "";
        toast.success(`Mailbox polled — ${d.processed || 0} new email(s)${rec}`);
        loadCarting();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || e.message || "Poll failed"))
      .finally(() => setPolling(false));
  };
  const [pendingOnly, setPendingOnly] = useState(false);
  const [needsEmailOnly, setNeedsEmailOnly] = useState(false);
  const [revisedOnly, setRevisedOnly] = useState(false);
  const [delayedOnly, setDelayedOnly] = useState(false);
  const [withCutoffOnly, setWithCutoffOnly] = useState(false);
  const [openOnly, setOpenOnly] = useState(true);
  const [msbFrom, setMsbFrom] = useState("");
  const [msbTo, setMsbTo] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const onSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };
  // Windowed rendering: mount only the first `visibleCount` rows, grow as a sentinel scrolls into
  // view. Keeps initial paint fast on large worklists (thousands of POs). Selection/search/sort/
  // export all still operate on the FULL `displayed` array below — only the row map is sliced.
  const [visibleCount, setVisibleCount] = useState(150);
  const sentinelRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/cfs/worklist", { params: { customer: customer || undefined } })
      .then((r) => { setRows(r.data.rows || []); setCustomers(r.data.customers || []); setSelected(new Set()); })
      .catch(() => toast.error("Failed to load CFS worklist"))
      .finally(() => setLoading(false));
  };
  const loadCarting = () => api.get("/cfs/carting/pending").then((r) => setCarting(r.data.rows || [])).catch(() => {});
  useEffect(() => { load(); }, [customer, tick]);
  useEffect(() => { loadCarting(); }, [tick]);

  const approveCarting = (id, pono, date) =>
    api.post(`/cfs/carting/${id}/approve`, { ms_pono: pono, cfs_cutoff_date: date })
      .then((r) => { toast.success(`CFS cut-off saved to PO ${r.data.ms_pono} · must ship by ${fmtDate(r.data.must_ship_by)}`); load(); loadCarting(); })
      .catch((e) => { toast.error(e?.response?.data?.detail || "Approve failed"); throw e; });
  const dismissCarting = (id) =>
    api.post(`/cfs/carting/${id}/reject`, { reason: "dismissed from review" })
      .then(() => { toast.success("Carting order dismissed"); loadCarting(); })
      .catch((e) => { toast.error("Dismiss failed"); throw e; });
  const openCartingImage = (id) =>
    api.get(`/cfs/carting/${id}/image`, { responseType: "blob" })
      .then((r) => window.open(URL.createObjectURL(r.data), "_blank"))
      .catch(() => toast.error("No image available"));

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = rows;
    if (s) {
      out = out.filter((r) =>
        [r.po_number, r.ms_pono, r.cust_po2, r.customer_name, ...r.items.map((i) => i.icode), ...r.items.map((i) => i.iname)]
          .join(" ").toLowerCase().includes(s));
    }
    // "Open POs only" (default ON): hide POs that have shipped out — nothing left to ship, either by
    // the PO's own open qty OR because the ERP has fully dispatched/invoiced every line (they only
    // linger because a cut-off is saved). Toggle OFF to see them for audit.
    if (openOnly) out = out.filter((r) => !r.closed_with_cutoff);
    return out;
  }, [rows, q, openOnly]);

  const toggle = (pono) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(pono) ? n.delete(pono) : n.add(pono);
    return n;
  });
  const toggleExpand = (pono) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(pono) ? n.delete(pono) : n.add(pono);
    return n;
  });
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.ms_pono)), [rows, selected]);

  // Pending carting orders keyed by every candidate PO so the approval renders inline on the row.
  const cartingByPono = useMemo(() => {
    const m = {};
    for (const c of carting) {
      const ponos = new Set();
      if (c.matched_ms_pono) ponos.add(String(c.matched_ms_pono));
      (c.match_candidates || []).forEach((x) => x.ms_pono && ponos.add(String(x.ms_pono)));
      ponos.forEach((p) => { (m[p] = m[p] || []).push(c); });
    }
    return m;
  }, [carting]);
  const shownPonos = useMemo(() => new Set(filtered.map((r) => String(r.ms_pono))), [filtered]);
  // Carting orders with no matching PO in the current view → shown as their own in-table rows.
  const orphanCarting = useMemo(() => carting.filter((c) => {
    const ponos = [c.matched_ms_pono, ...(c.match_candidates || []).map((x) => x.ms_pono)].filter(Boolean).map(String);
    return ponos.length === 0 || !ponos.some((p) => shownPonos.has(p));
  }), [carting, shownPonos]);

  // Apply the "suggested cut-offs only" filter + column sorting for the visible rows.
  const displayed = useMemo(() => {
    const packNum = (r) => (Number(r.tot_cartons) || 0) + (Number(r.tot_pallets) || 0);
    let list = pendingOnly
      ? filtered.filter((r) => (cartingByPono[String(r.ms_pono)] || []).length)
      : filtered;
    if (needsEmailOnly) list = list.filter((r) => r.email_state === "not_emailed" || r.email_state === "reemail_needed");
    if (revisedOnly) list = list.filter((r) => !!r.revision);
    if (delayedOnly) list = list.filter((r) => delayDays(r.must_ship_by) != null);
    if (withCutoffOnly) list = list.filter((r) => !!r.cfs_cutoff_date);
    if (msbFrom || msbTo) list = list.filter((r) => {
      if (!r.must_ship_by) return false;
      const d = String(r.must_ship_by).slice(0, 10);
      if (msbFrom && d < msbFrom) return false;
      if (msbTo && d > msbTo) return false;
      return true;
    });
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      const dateKeys = new Set(["po_date", "earliest_due", "cfs_cutoff_date", "must_ship_by"]);
      const numKeys = new Set(["tot_qty", "packnum", "delay"]);
      list = [...list].sort((a, b) => {
        let av, bv;
        if (sortKey === "packnum") { av = packNum(a); bv = packNum(b); }
        else if (sortKey === "delay") { av = delayDays(a.must_ship_by) || 0; bv = delayDays(b.must_ship_by) || 0; }
        else { av = a[sortKey]; bv = b[sortKey]; }
        if (dateKeys.has(sortKey)) {
          av = av ? new Date(av).getTime() : (dir === 1 ? Infinity : -Infinity);
          bv = bv ? new Date(bv).getTime() : (dir === 1 ? Infinity : -Infinity);
          return (av - bv) * dir;
        }
        if (numKeys.has(sortKey)) return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
        return String(av || "").localeCompare(String(bv || "")) * dir;
      });
    }
    return list;
  }, [filtered, pendingOnly, needsEmailOnly, revisedOnly, delayedOnly, withCutoffOnly, msbFrom, msbTo, sortKey, sortDir, cartingByPono]);

  // Reset the window whenever the displayed set changes (filter/sort/search/customer switch).
  useEffect(() => { setVisibleCount(150); }, [displayed]);
  // Grow the window when the bottom sentinel nears the viewport (preload 800px early for smoothness).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setVisibleCount((c) => Math.min(c + 200, displayed.length));
    }, { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [displayed.length, visibleCount]);

  // Export the currently displayed POs as a planning sheet — one line PER ITEM so open quantities
  // are visible for load planning. Respects the active filters (e.g. "With cut-off").
  const downloadPlan = () => {
    const flat = [];
    displayed.forEach((r) => {
      const items = (r.items && r.items.length) ? r.items : [{ icode: "", iname: "", open_qty: "" }];
      items.forEach((it) => flat.push({
        po_number: r.po_number || "", ms_pono: r.ms_pono || "", customer_name: r.customer_name || "",
        cust_po2: r.cust_po2 || "", revision: r.revision || "",
        cfs_cutoff_date: r.cfs_cutoff_date || "", must_ship_by: r.must_ship_by || "",
        delay: delayDays(r.must_ship_by), tot_cartons: r.tot_cartons || 0, tot_pallets: r.tot_pallets || 0,
        icode: it.icode || "", iname: it.iname || "", open_qty: Number(it.open_qty) || 0,
      }));
    });
    if (!flat.length) { toast.error("No POs to download for the current filters."); return; }
    const cols = [
      { header: "PO Number", value: (r) => r.po_number },
      { header: "Order No (ms_pono)", value: (r) => r.ms_pono },
      { header: "Customer", value: (r) => r.customer_name },
      { header: "End Customer PO", value: (r) => r.cust_po2 },
      { header: "Rev", value: (r) => r.revision },
      ...(isAdmin ? [{ header: "CFS Cut-Off", value: (r) => xlsxDate(r.cfs_cutoff_date) }] : []),
      { header: "Must Ship By", value: (r) => xlsxDate(r.must_ship_by) },
      { header: "Delay (days)", value: (r) => (r.delay == null ? "" : r.delay) },
      { header: "Item Code", value: (r) => r.icode },
      { header: "Item Name", value: (r) => r.iname },
      { header: "Open Qty", value: (r) => r.open_qty },
      { header: "Cartons", value: (r) => r.tot_cartons },
      { header: "Pallets", value: (r) => r.tot_pallets },
    ];
    exportRowsToXlsx("cfs-cutoff-plan", cols, flat, "CFS Cut-Off Plan");
    toast.success(`Downloaded ${flat.length} item line${flat.length > 1 ? "s" : ""} across ${displayed.length} PO${displayed.length > 1 ? "s" : ""}`);
  };

  const allShown = displayed.length > 0 && displayed.every((r) => selected.has(r.ms_pono));
  const toggleAll = () => setSelected(() => {
    if (allShown) return new Set();
    return new Set(displayed.map((r) => r.ms_pono));
  });

  const saveCutoff = (row, val) => {
    setSavingPono(row.ms_pono);
    api.put("/cfs/cutoff", { ms_pono: row.ms_pono, cfs_cutoff_date: val || "" })
      .then((r) => {
        setRows((prev) => prev.map((x) => x.ms_pono === row.ms_pono
          ? { ...x, cfs_cutoff_date: r.data.cfs_cutoff_date, must_ship_by: r.data.must_ship_by } : x));
        toast.success(val ? "CFS cut-off saved" : "CFS cut-off cleared");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Save failed"))
      .finally(() => setSavingPono(null));
  };

  // Non-admins don't see the row checkbox (email selection) nor the CFS Cut-Off column.
  // 9 base cols + CFS Cut-Off col (visible to admins/logistics) + email-select checkbox (logistics only).
  const colCount = 9 + (isAdmin ? 1 : 0) + (canEdit ? 1 : 0);

  return (
    <Layout>
      <div style={{ maxWidth: 1800, margin: "0 auto" }} data-testid="cfs-cutoff-page">
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <CalendarBlank size={26} weight="duotone" /> CFS Cut-Off
          </h1>
          <p style={{ fontSize: 13, color: "#64748b", margin: "6px 0 0", maxWidth: 780 }}>
            {isAdmin ? (
              <>Enter or update the CFS cut-off for open POs (Walmart, Walmart Canada, LG Sourcing, Legrand).
              The dispatch must be completed by
              <b> cut-off − 4 calendar days</b>, which becomes the OTD target for these POs. This date is
              app-only and is never written to the ERP.</>
            ) : (
              <>Plan open POs by their <b>Must Ship By</b> date. Use the date range to focus on a shipping window,
              then download an item-wise sheet with open quantities for load planning.</>
            )}
          </p>
        </div>

        {/* Filters + bulk email */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          <select data-testid="cfs-customer-filter" value={customer} onChange={(e) => setCustomer(e.target.value)}
            style={{ padding: "8px 12px", fontSize: 13, border: BORDER, borderRadius: 8, background: "white", minWidth: 220 }}>
            <option value="">All customers ({rows.length})</option>
            {customers.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
            <MagnifyingGlass size={16} style={{ position: "absolute", left: 12, top: 11, color: "#94a3b8" }} />
            <input data-testid="cfs-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search PO, end-customer PO, item…"
              style={{ width: "100%", padding: "9px 12px 9px 34px", fontSize: 13, border: BORDER, borderRadius: 8 }} />
          </div>
          <button data-testid="cfs-poll-mailbox" onClick={pollMailbox} disabled={polling}
            title="Check the intake mailbox now for new carting-order CFS cut-offs"
            style={{ display: isAdmin ? "flex" : "none", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8, border: BORDER, background: "white", color: "#334155", cursor: polling ? "not-allowed" : "pointer", opacity: polling ? 0.6 : 1 }}>
            <ArrowsClockwise size={15} weight="bold" /> {polling ? "Polling…" : "Poll mailbox"}
          </button>
          {strictAdmin && (
            <div data-testid="cfs-backfill-group" title="Re-read carting/CFS emails received on/after this date (for notices that arrived before the current poll window). Already-confirmed, unchanged cut-offs are left alone."
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 6px 3px 10px", border: BORDER, borderRadius: 8, background: "white" }}>
              <CalendarBlank size={15} weight="bold" style={{ color: "#64748b" }} />
              <input data-testid="cfs-backfill-date" type="date" value={backfillDate} max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setBackfillDate(e.target.value)}
                style={{ fontSize: 13, padding: "5px 6px", border: BORDER, borderRadius: 6, background: "white" }} />
              <button data-testid="cfs-backfill-btn" onClick={runBackfill} disabled={backfilling || !backfillDate}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", fontSize: 13, fontWeight: 700, borderRadius: 6, border: "none", background: "#4f46e5", color: "white", cursor: (backfilling || !backfillDate) ? "not-allowed" : "pointer", opacity: (backfilling || !backfillDate) ? 0.6 : 1 }}>
                {backfilling ? "Backfilling…" : "Backfill from date"}
              </button>
            </div>
          )}
          <button data-testid="cfs-pending-toggle" onClick={() => setPendingOnly((v) => !v)} disabled={carting.length === 0}
            title="Show only POs with a carting-order cut-off suggested for approval"
            style={{ display: isAdmin ? "flex" : "none", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8,
              border: pendingOnly ? "none" : BORDER,
              background: pendingOnly ? "#f59e0b" : (carting.length ? "#fffbeb" : "#f1f5f9"),
              color: pendingOnly ? "white" : (carting.length ? "#92400e" : "#94a3b8"),
              cursor: carting.length ? "pointer" : "not-allowed" }}>
            <Funnel size={15} weight={pendingOnly ? "fill" : "regular"} /> Suggested cut-offs ({carting.length})
          </button>
          <button data-testid="cfs-needs-email-toggle" onClick={() => setNeedsEmailOnly((v) => !v)}
            title="Show only POs whose CFS cut-off has not been emailed to the team (or needs re-emailing)"
            style={{ display: isAdmin ? "flex" : "none", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8,
              border: needsEmailOnly ? "none" : BORDER,
              background: needsEmailOnly ? "#0f766e" : "white",
              color: needsEmailOnly ? "white" : "#334155", cursor: "pointer" }}>
            <EnvelopeSimple size={15} /> Not emailed ({rows.filter((r) => r.email_state === "not_emailed" || r.email_state === "reemail_needed").length})
          </button>
          <button data-testid="cfs-revised-toggle" onClick={() => setRevisedOnly((v) => !v)}
            title="Show only POs where a newer carting order revises the saved CFS cut-off"
            style={{ display: isAdmin ? "flex" : "none", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8,
              border: revisedOnly ? "none" : BORDER,
              background: revisedOnly ? "#c2410c" : "white",
              color: revisedOnly ? "white" : "#334155", cursor: "pointer" }}>
            ⟳ Revised ({rows.filter((r) => !!r.revision).length})
          </button>
          <button data-testid="cfs-delayed-toggle" onClick={() => setDelayedOnly((v) => !v)}
            title="Show only POs already past their Must Ship By date (not yet shipped)"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8,
              border: delayedOnly ? "none" : BORDER,
              background: delayedOnly ? "#b91c1c" : "white",
              color: delayedOnly ? "white" : "#334155", cursor: "pointer" }}>
            <Funnel size={15} weight={delayedOnly ? "fill" : "regular"} /> Delayed ({rows.filter((r) => delayDays(r.must_ship_by) != null).length})
          </button>
          <button data-testid="cfs-with-cutoff-toggle" onClick={() => setWithCutoffOnly((v) => !v)}
            title="Show only POs that already have a CFS cut-off date set"
            style={{ display: isAdmin ? "flex" : "none", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8,
              border: withCutoffOnly ? "none" : BORDER,
              background: withCutoffOnly ? "#166534" : "white",
              color: withCutoffOnly ? "white" : "#334155", cursor: "pointer" }}>
            <Funnel size={15} weight={withCutoffOnly ? "fill" : "regular"} /> With cut-off ({rows.filter((r) => !!r.cfs_cutoff_date).length})
          </button>
          <button data-testid="cfs-open-only-toggle" onClick={() => setOpenOnly((v) => !v)}
            title="Show only Open POs — hides POs that have shipped out (fully dispatched per the Dispatch Register / ERP), which only linger because a cut-off is saved. On by default."
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8,
              border: openOnly ? "none" : BORDER,
              background: openOnly ? "#166534" : "white",
              color: openOnly ? "white" : "#334155", cursor: "pointer" }}>
            <Funnel size={15} weight={openOnly ? "fill" : "regular"} /> Open POs only ({rows.filter((r) => r.closed_with_cutoff).length} shipped {openOnly ? "hidden" : "shown"})
          </button>
          <div data-testid="cfs-msb-range" title="Filter POs by their Must Ship By date — applies to the table and the Excel download"
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 10px", border: BORDER, borderRadius: 8, background: "white" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#475569", whiteSpace: "nowrap" }}>Must Ship By</span>
            <input data-testid="cfs-msb-from" type="date" value={msbFrom} onChange={(e) => setMsbFrom(e.target.value)}
              style={{ fontSize: 13, padding: "5px 6px", border: BORDER, borderRadius: 6, background: "white" }} />
            <span style={{ fontSize: 12, color: "#94a3b8" }}>→</span>
            <input data-testid="cfs-msb-to" type="date" value={msbTo} onChange={(e) => setMsbTo(e.target.value)}
              style={{ fontSize: 13, padding: "5px 6px", border: BORDER, borderRadius: 6, background: "white" }} />
            {(msbFrom || msbTo) && (
              <button data-testid="cfs-msb-clear" onClick={() => { setMsbFrom(""); setMsbTo(""); }} title="Clear date range"
                style={{ border: "none", background: "none", cursor: "pointer", color: "#94a3b8", lineHeight: 0, padding: 2 }}>
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
          <button data-testid="cfs-download-plan" onClick={downloadPlan}
            title="Download the shown POs (one line per item, with open quantities) as an Excel planning sheet"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8,
              border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", cursor: "pointer" }}>
            <DownloadSimple size={15} weight="bold" /> Download plan (Excel)
          </button>
          <button data-testid="cfs-email-selected" disabled={selectedRows.length === 0} onClick={() => setEmailOpen(true)}
            style={{ display: canEdit ? "flex" : "none", alignItems: "center", gap: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8, border: "none", background: selectedRows.length ? "var(--omh-primary,#2A5C9B)" : "#cbd5e1", color: "white", cursor: selectedRows.length ? "pointer" : "not-allowed" }}>
            <EnvelopeSimple size={16} /> Email selected ({selectedRows.length})
          </button>
        </div>

        {/* Table */}
        <div style={{ border: BORDER, borderRadius: 12, overflow: "hidden", background: "white" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="cfs-table">
              <thead>
                <tr>
                  {canEdit && (
                    <th style={{ ...th, textAlign: "center", width: 38 }}>
                      <input type="checkbox" data-testid="cfs-select-all" checked={allShown} onChange={toggleAll} />
                    </th>
                  )}
                  <SortTh label="Customer" k="customer_name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="PO No." k="po_number" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="End-Cust PO" k="cust_po2" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="PO Date" k="po_date" align="center" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="Earliest Due" k="earliest_due" align="center" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="Total Qty" k="tot_qty" align="right" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="Cartons / Pallets" k="packnum" align="center" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  {isAdmin && <SortTh label="CFS Cut-Off" k="cfs_cutoff_date" align="center" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />}
                  <SortTh label="Must Ship By (−4 bd)" k="must_ship_by" align="center" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="Delay (days)" k="delay" align="center" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td style={{ ...td, textAlign: "center", padding: 30, color: "#94a3b8" }} colSpan={colCount}>Loading…</td></tr>}
                {!loading && canEdit && !withCutoffOnly && !revisedOnly && !delayedOnly && orphanCarting.map((c) => (
                  <tr key={`orphan-${c.id}`} data-testid={`carting-orphan-${c.id}`} style={{ background: "#fffbeb" }}>
                    <td style={{ ...td, textAlign: "center" }} title="Carting order — no matching PO in the current view">
                      <span style={{ fontSize: 16, color: "#f59e0b" }}>●</span>
                    </td>
                    <td style={td} colSpan={2}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>Carting order · PO {c.po_number || "—"}</div>
                      <div style={{ fontSize: 11, color: "#a16207" }}>
                        {c.consignee || ""}{c.consignee ? " · " : ""}{c.match_type === "none" ? "no PO match — enter ms_pono" : "PO not in current view"}
                      </div>
                    </td>
                    <td style={td} colSpan={8}>
                      <InlineCartingApprove c={c} candidates={c.match_candidates} allowManual
                        onApprove={approveCarting} onDismiss={dismissCarting} onView={openCartingImage} />
                    </td>
                  </tr>
                ))}
                {!loading && displayed.length === 0 && ((withCutoffOnly || revisedOnly || delayedOnly) || !isAdmin || orphanCarting.length === 0) && <tr><td style={{ ...td, textAlign: "center", padding: 30, color: "#94a3b8" }} colSpan={colCount}>{withCutoffOnly ? "No POs have a CFS cut-off date set." : (pendingOnly ? "No suggested cut-offs pending approval." : "No open POs found.")}</td></tr>}
                {!loading && displayed.slice(0, visibleCount).map((r, idx) => (
                  <Fragment key={`${r.ms_pono}-${idx}`}>
                  <tr data-testid={`cfs-row-${r.ms_pono}`} style={selected.has(r.ms_pono) ? { background: "#eff6ff" } : undefined}>
                    {canEdit && (
                      <td style={{ ...td, textAlign: "center" }}>
                        <input type="checkbox" data-testid={`cfs-select-${r.ms_pono}`} checked={selected.has(r.ms_pono)} onChange={() => toggle(r.ms_pono)} />
                      </td>
                    )}
                    <td style={td}>{r.customer_name}</td>
                    <td style={{ ...td, fontWeight: 700 }}>
                      <button data-testid={`cfs-expand-${r.ms_pono}`} onClick={() => toggleExpand(r.ms_pono)}
                        title="Show item / PO line details"
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "none", cursor: "pointer", font: "inherit", fontWeight: 700, color: "var(--omh-primary,#2A5C9B)" }}>
                        {expanded.has(r.ms_pono) ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
                        {r.po_number}
                        <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>({r.open_lines})</span>
                        {r.closed_with_cutoff && (
                          <span data-testid={`cfs-closed-badge-${r.ms_pono}`} title={`Already ex-factory (${r.closed_reason}) — shown because a cut-off is saved`}
                            style={{ fontSize: 10, fontWeight: 700, color: "#166534", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 5, padding: "1px 6px", textTransform: "uppercase" }}>
                            {r.closed_reason === "superseded" ? "Superseded" : "Shipped"}
                          </span>
                        )}
                      </button>
                    </td>
                    <td style={td}>{r.cust_po2 || "—"}</td>
                    <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>{r.po_date ? fmtDate(r.po_date) : "—"}</td>
                    <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>{r.earliest_due ? fmtDate(r.earliest_due) : "—"}</td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{fmtNum(r.tot_qty)}</td>
                    <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>{packLabel(r)}</td>
                    {isAdmin && (
                    <td style={{ ...td, textAlign: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                        <CutoffCell testid={`cfs-date-${r.ms_pono}`} value={r.cfs_cutoff_date} canEdit={canEdit}
                          saving={savingPono === r.ms_pono} onSave={(v) => saveCutoff(r, v)} />
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "center" }}>
                          <EmailStateChip state={r.email_state} lastSentAt={r.last_email_sent_at} />
                          <RevisedChip revision={r.revision} />
                          {(r.splits || []).length > 0 && (
                            <span data-testid={`cfs-splits-chip-${r.ms_pono}`} title="This PO has CY/CFS shipment splits — expand to view"
                              style={{ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 800, background: "#ede9fe", color: "#6d28d9" }}>
                              {r.splits.length} split{r.splits.length > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        {(cartingByPono[String(r.ms_pono)] || []).map((c) => (
                          <InlineCartingApprove key={c.id} c={c} pono={r.ms_pono}
                            onApprove={approveCarting} onDismiss={dismissCarting} onView={openCartingImage} />
                        ))}
                      </div>
                    </td>
                    )}
                    <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap", fontWeight: 700, color: r.must_ship_by ? "#b45309" : "#cbd5e1" }}>
                      {r.must_ship_by ? fmtDate(r.must_ship_by) : "—"}
                    </td>
                    <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }} data-testid={`cfs-delay-${r.ms_pono}`}>
                      {delayDays(r.must_ship_by) != null
                        ? <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: 12, fontWeight: 800, background: "#fee2e2", color: "#b91c1c" }}>{delayDays(r.must_ship_by)}d late</span>
                        : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>
                  </tr>
                  {expanded.has(r.ms_pono) && (
                    <tr data-testid={`cfs-detail-${r.ms_pono}`}>
                      <td colSpan={colCount} style={{ padding: "0 12px 12px 12px", background: "#f8fafc", borderBottom: BORDER }}>
                        <div style={{ padding: "10px 0 4px", fontSize: 11, fontWeight: 700, color: "#64748b" }}>
                          PO {r.po_number} · End-Cust PO {r.cust_po2 || "—"} · {r.customer_name} — {r.open_lines} open line(s)
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse", background: "white", border: BORDER, borderRadius: 8 }}>
                          <thead>
                            <tr>
                              <th style={{ ...th, background: "#eef2f7" }}>Item No.</th>
                              <th style={{ ...th, background: "#eef2f7" }}>Description</th>
                              <th style={{ ...th, background: "#eef2f7", textAlign: "center" }}>Due Date</th>
                              <th style={{ ...th, background: "#eef2f7", textAlign: "right" }}>Open Qty</th>
                              <th style={{ ...th, background: "#eef2f7", textAlign: "right" }}>Cartons</th>
                              <th style={{ ...th, background: "#eef2f7", textAlign: "right" }}>Pallets</th>
                              {isAdmin && <th style={{ ...th, background: "#eef2f7", textAlign: "center" }}>CFS Cut-Off</th>}
                              <th style={{ ...th, background: "#eef2f7", textAlign: "center" }}>Must Ship By</th>
                              <th style={{ ...th, background: "#eef2f7", textAlign: "center" }}>Delay (days)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(r.items || []).map((it, i) => (
                              <tr key={i}>
                                <td style={{ ...td, fontWeight: 600 }}>{it.icode || "—"}</td>
                                <td style={td}>{it.iname || "—"}</td>
                                <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>{it.due_date ? fmtDate(it.due_date) : "—"}</td>
                                <td style={{ ...td, textAlign: "right" }}>{fmtNum(it.open_qty)}</td>
                                <td style={{ ...td, textAlign: "right" }}>{it.cartons ? fmtNum(it.cartons) : "—"}</td>
                                <td style={{ ...td, textAlign: "right" }}>{it.pallets ? fmtNum(it.pallets) : "—"}</td>
                                {isAdmin && <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap", color: r.cfs_cutoff_date ? "#0f172a" : "#cbd5e1" }}>{r.cfs_cutoff_date ? fmtDate(r.cfs_cutoff_date) : "—"}</td>}
                                <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap", fontWeight: 700, color: r.must_ship_by ? "#b45309" : "#cbd5e1" }}>{r.must_ship_by ? fmtDate(r.must_ship_by) : "—"}</td>
                                <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>{delayDays(r.must_ship_by) != null ? <span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, background: "#fee2e2", color: "#b91c1c" }}>{delayDays(r.must_ship_by)}d late</span> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <SplitsEditor row={r} onSaved={load} canEdit={canEdit} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {!loading && displayed.length > visibleCount && (
                  <tr ref={sentinelRef} data-testid="cfs-load-more-sentinel">
                    <td colSpan={colCount} style={{ ...td, textAlign: "center", padding: 16, color: "#94a3b8", fontSize: 12 }}>
                      Showing {visibleCount} of {displayed.length} POs — scroll to load more…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {emailOpen && selectedRows.length > 0 && <EmailModal rows={selectedRows} onClose={() => setEmailOpen(false)} />}
    </Layout>
  );
}
